import { useEffect, useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import {
  Activity,
  AlertCircle,
  CheckCircle2,
  Film,
  FileVideo,
  Flame,
  Gauge,
  Loader2,
  Paperclip,
  Wrench,
  X,
  Zap,
} from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/shared/components/ui/button';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { cn } from '@/shared/lib/cn';
import {
  cancelPuestaMarchaUploadSession,
  fetchVentaElectrico,
  finalizePuestaMarcha,
  uploadPuestaMarchaVideo,
  type MantenimientoRow,
  type PuestaMarchaPicker,
} from '../api';

/**
 * Puesta Marcha dialog. Per PowerApps Screen_DetalleMantenimiento, but
 * with a tightened gating rule:
 *
 *   - Pure-electric venta (every article `Electrico_ART = 'SI'`):
 *     combustion videos disabled, only optional multimedia accepted.
 *   - Pure-gas venta: three combustion videos required.
 *   - Mixed venta (≥1 electric + ≥1 gas): combustion videos still
 *     required — they apply to the gas oven(s). The dialog lists the
 *     specific oven(s) so the admin knows what they're documenting.
 *   - A 4th picker (multimedia, optional) is always available.
 *
 * PowerApps treated any electric match as "skip all videos", which let
 * mixed ventas finalize with no docs for the gas oven. We don't.
 *
 * The "eléctrico" flag is derived server-side from a join against
 * `99.ABM_Articulos`; the dialog asks via `GET /electrico` and renders
 * accordingly. There's no manual toggle.
 *
 * Uploads bypass our backend entirely. The server hands out a Graph
 * upload-session URL and the browser PUTs the bytes in 10 MB chunks
 * directly to Microsoft. After the chunks land, a single `finalize`
 * call flips `Status_RO='Cerrado'` + `PuestaMarchaCargada_RO='SI'`.
 */

type SlotState = {
  file: File | null;
  progress: number; // 0..1
  uploaded: boolean;
  error: string | null;
  /** Drive-item id returned by the upload session. */
  driveItemId: string | null;
  /** Active upload-session URL, kept so we can DELETE it on cancel. */
  uploadUrl: string | null;
};

const ALL_PICKERS: PuestaMarchaPicker[] = [
  'instalacion',
  'llama',
  'regulador',
  'multimedia',
];

const PICKER_META: Record<
  PuestaMarchaPicker,
  { label: string; helpText: string; icon: React.ElementType; accept: string }
> = {
  instalacion: {
    label: 'Video instalación c/ Manómetro y Regulador',
    helpText: 'Video del horno ya instalado y conectado.',
    icon: Wrench,
    accept: 'video/*',
  },
  llama: {
    label: 'Video flama superior e inferior',
    helpText: 'Ensayo de combustión en funcionamiento.',
    icon: Flame,
    accept: 'video/*',
  },
  regulador: {
    label: 'Video o foto regulación sopladores',
    helpText: 'Verificación del regulador / sopladores.',
    icon: Gauge,
    accept: 'video/*,image/*',
  },
  multimedia: {
    label: 'Multimedia relacionada a la instalación',
    helpText: 'Cualquier archivo extra (foto, PDF, video).',
    icon: Paperclip,
    accept: 'video/*,image/*,application/pdf',
  },
};

const blankSlot = (): SlotState => ({
  file: null,
  progress: 0,
  uploaded: false,
  error: null,
  driveItemId: null,
  uploadUrl: null,
});

export function PuestaMarchaDialog({
  summary,
  onClose,
}: {
  summary: MantenimientoRow;
  onClose: () => void;
}) {
  const qc = useQueryClient();

  // Server detects "eléctrico" from the venta's articles (PowerApps's
  // `Collecteletrico` filter). We render the dialog around this answer.
  const electricoQ = useQuery({
    queryKey: ['mantenimiento', 'electrico', summary.id],
    queryFn: () => fetchVentaElectrico(summary.id),
    staleTime: 60_000,
  });
  const electrico = electricoQ.data?.electrico ?? false;
  const electricArticulos = electricoQ.data?.electricArticulos ?? [];
  const gasArticulos = electricoQ.data?.gasArticulos ?? [];
  // Mixed = at least one of each. Drives a callout naming the gas
  // oven(s) the videos apply to so the admin doesn't wonder which
  // oven they're documenting on a multi-line venta.
  const isMixed = electricArticulos.length > 0 && gasArticulos.length > 0;

  const [slots, setSlots] = useState<Record<PuestaMarchaPicker, SlotState>>({
    instalacion: blankSlot(),
    llama: blankSlot(),
    regulador: blankSlot(),
    multimedia: blankSlot(),
  });
  const [phase, setPhase] = useState<'idle' | 'uploading' | 'finalizing' | 'done'>(
    'idle',
  );
  const [finalizeError, setFinalizeError] = useState<string | null>(null);
  // Track which slots are currently disabled / required so we can
  // re-evaluate when `electricoQ` resolves without flicker.
  const requiredSlots: PuestaMarchaPicker[] = electrico
    ? []
    : ['instalacion', 'llama', 'regulador'];
  const disabledSlots: PuestaMarchaPicker[] = electrico
    ? ['instalacion', 'llama', 'regulador']
    : [];

  // If the user toggles electric (refresh after a catalog change while
  // dialog is open, edge-case), drop any files we'd staged for slots
  // that just became disabled.
  useEffect(() => {
    setSlots((prev) => {
      let dirty = false;
      const next = { ...prev };
      for (const s of disabledSlots) {
        if (next[s].file || next[s].uploaded) {
          next[s] = blankSlot();
          dirty = true;
        }
      }
      return dirty ? next : prev;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [electrico]);

  const setSlotFile = (slot: PuestaMarchaPicker, file: File | null) => {
    setSlots((prev) => ({
      ...prev,
      [slot]: { ...blankSlot(), file },
    }));
  };

  const allRequiredChosen = requiredSlots.every((s) => slots[s].file != null);
  const allRequiredUploaded = requiredSlots.every((s) => slots[s].uploaded);
  const busy = phase === 'uploading' || phase === 'finalizing';

  /**
   * Returns the resulting driveItemId on success, or `null` on failure
   * (with the slot-card error already set). Returning the id directly
   * lets `handleStart` build the finalize payload from a local map
   * instead of reading state through a ref — refs only update on
   * render, and React's setState batching means the ref can lag the
   * last slot's id behind the loop.
   */
  const uploadSlot = async (
    slot: PuestaMarchaPicker,
    file: File,
  ): Promise<string | null> => {
    try {
      const result = await uploadPuestaMarchaVideo({
        ventaId: summary.id,
        slot,
        file,
        onSessionUrl: (url) => {
          setSlots((prev) => ({
            ...prev,
            [slot]: { ...prev[slot], uploadUrl: url },
          }));
        },
        onProgress: ({ loaded, total }) => {
          setSlots((prev) => ({
            ...prev,
            [slot]: { ...prev[slot], progress: total > 0 ? loaded / total : 0 },
          }));
        },
      });
      if (result.outcome === 'uploaded') {
        setSlots((prev) => ({
          ...prev,
          [slot]: {
            ...prev[slot],
            progress: 1,
            uploaded: true,
            error: null,
            driveItemId: result.driveItemId,
            uploadUrl: null,
          },
        }));
        return result.driveItemId;
      }
      setSlots((prev) => ({
        ...prev,
        [slot]: { ...prev[slot], error: result.message, uploadUrl: null },
      }));
      return null;
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error desconocido';
      setSlots((prev) => ({
        ...prev,
        [slot]: { ...prev[slot], error: msg, uploadUrl: null },
      }));
      return null;
    }
  };

  const handleStart = async () => {
    setPhase('uploading');
    setFinalizeError(null);

    // Local map of every driveItemId we now hold for this run — seeded
    // with anything that was uploaded in a previous attempt and not
    // re-uploaded. Built up as each upload completes so the finalize
    // payload doesn't depend on a ref that lags React's render cycle.
    const driveItemIds: Partial<Record<PuestaMarchaPicker, string>> = {};
    for (const slot of ALL_PICKERS) {
      const id = slots[slot].driveItemId;
      if (id) driveItemIds[slot] = id;
    }

    const slotsToUpload: Array<{ slot: PuestaMarchaPicker; file: File }> = [];
    for (const slot of ALL_PICKERS) {
      const s = slots[slot];
      if (s.file && !s.uploaded) {
        slotsToUpload.push({ slot, file: s.file });
      }
    }

    for (const { slot, file } of slotsToUpload) {
      const id = await uploadSlot(slot, file);
      if (!id) {
        // Either required (must halt) or optional (used to silently
        // skip — that hid bugs where the file was lost before
        // finalize). Now we halt and surface the error either way; the
        // user can retry the failed slot or remove it before
        // continuing.
        setPhase('idle');
        toast.error(`Falló la subida de "${PICKER_META[slot].label}"`);
        return;
      }
      driveItemIds[slot] = id;
    }

    setPhase('finalizing');
    try {
      const uploadedSlots = (Object.keys(driveItemIds) as PuestaMarchaPicker[]).filter(
        (s) => driveItemIds[s],
      );
      const result = await finalizePuestaMarcha(summary.id, {
        uploadedSlots,
        slotDriveItemIds: driveItemIds,
      });
      if (result.outcome === 'finalized') {
        setPhase('done');
        toast.success('Puesta en marcha registrada', {
          description:
            result.cerrada === false
              ? 'La venta tiene E-cheqs sin cobrar: se cierra cuando esté paga.'
              : undefined,
        });
        qc.invalidateQueries({ queryKey: ['mantenimiento'] });
        qc.invalidateQueries({ queryKey: ['ventas'] });
        qc.invalidateQueries({ queryKey: ['home', 'operaciones'] });
        onClose();
      } else if (result.outcome === 'missing_videos') {
        setFinalizeError(result.message);
        setPhase('idle');
      } else {
        setFinalizeError(result.message);
        setPhase('idle');
      }
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Error desconocido';
      setFinalizeError(msg);
      setPhase('idle');
    }
  };

  const handleClose = () => {
    if (busy) return;
    // Best-effort: cancel any in-flight upload sessions so Graph drops
    // the half-uploaded files faster than its 10-min default GC.
    for (const s of ALL_PICKERS) {
      const url = slots[s].uploadUrl;
      if (url) void cancelPuestaMarchaUploadSession(summary.id, url);
    }
    onClose();
  };

  const canStart =
    !busy &&
    phase !== 'done' &&
    !electricoQ.isPending &&
    // For electric ovens, we can finalize even with no files at all
    // (PowerApps lets the admin just close). Non-electric ovens need
    // every required slot picked.
    (electrico ? true : allRequiredChosen) &&
    !allRequiredUploaded;

  return (
    <Dialog open onOpenChange={(o) => !o && handleClose()}>
      <DialogContent
        className="max-w-2xl"
        // Exception to the global overlay convention: during 'uploading'
        // each SlotCard renders its own per-file progress (size, percent,
        // filename) — covering them with a blurred fullscreen spinner
        // hides the very feedback the user needs. The overlay only fires
        // for the brief 'finalizing' round-trip after all files are up.
        processing={phase === 'finalizing' && 'Finalizando puesta en marcha…'}
      >
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Activity className="h-4 w-4" aria-hidden />
            Puesta en Marcha · {summary.fields.Cliente_RO || 'Cliente'}
          </DialogTitle>
          <DialogDescription>Documentación de instalación.</DialogDescription>
        </DialogHeader>
        <DialogBody className="space-y-4">
          {electricoQ.isPending ? (
            <div className="flex items-center justify-center py-6 text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
            </div>
          ) : electricoQ.isError ? (
            <div className="rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
              No se pudo determinar si la venta es eléctrica. Reabrí el diálogo.
            </div>
          ) : (
            <>
              {electrico && (
                <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  <Zap className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                  <span>
                    Esta venta es 100% eléctrica — los videos de instalación,
                    flama y regulador no aplican. Solo se acepta multimedia
                    opcional.
                  </span>
                </div>
              )}
              {isMixed && (
                <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-xs text-amber-900">
                  <Zap className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                  <div className="space-y-1">
                    <p>
                      Esta venta combina artículos eléctricos y a gas. Los
                      videos de combustión aplican únicamente al/los horno(s)
                      a gas:
                    </p>
                    <ul className="ml-4 list-disc space-y-0.5 font-medium">
                      {gasArticulos.map((nombre) => (
                        <li key={nombre}>{nombre}</li>
                      ))}
                    </ul>
                    {electricArticulos.length > 0 && (
                      <p className="text-[11px] opacity-80">
                        Eléctricos (sin videos):{' '}
                        {electricArticulos.join(', ')}.
                      </p>
                    )}
                  </div>
                </div>
              )}

              <section className="space-y-2">
                <h3 className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">
                  Archivos
                </h3>
                <div className="space-y-2">
                  {ALL_PICKERS.map((slot) => {
                    const meta = PICKER_META[slot];
                    const state = slots[slot];
                    const isRequired = requiredSlots.includes(slot);
                    const isDisabled = disabledSlots.includes(slot) || busy;
                    return (
                      <SlotCard
                        key={slot}
                        slot={slot}
                        label={meta.label}
                        icon={meta.icon}
                        helpText={meta.helpText}
                        accept={meta.accept}
                        required={isRequired}
                        disabled={isDisabled}
                        state={state}
                        onFileChange={(f) => setSlotFile(slot, f)}
                      />
                    );
                  })}
                </div>
              </section>

              {finalizeError && (
                <div className="flex items-start gap-2 rounded-md border border-destructive/30 bg-destructive/5 px-3 py-2 text-sm text-destructive">
                  <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                  <span>{finalizeError}</span>
                </div>
              )}

              <div className="flex items-start gap-2 rounded-md border border-sky-200 bg-sky-50 px-3 py-2 text-xs text-sky-800">
                <AlertCircle className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                <span>
                  Al finalizar, la venta pasa a estado <strong>Cerrado</strong> y
                  queda lista para emitir el certificado de garantía.
                </span>
              </div>
            </>
          )}
        </DialogBody>
        <DialogFooter>
          <Button variant="ghost" onClick={handleClose} disabled={busy}>
            Cancelar
          </Button>
          <Button onClick={handleStart} disabled={!canStart} className="gap-2">
            {phase === 'uploading' ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                Subiendo…
              </>
            ) : (
              <>
                <CheckCircle2 className="h-4 w-4" aria-hidden />
                Iniciar carga
              </>
            )}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function SlotCard({
  slot,
  label,
  icon: Icon,
  helpText,
  accept,
  required,
  disabled,
  state,
  onFileChange,
}: {
  slot: PuestaMarchaPicker;
  label: string;
  icon: React.ElementType;
  helpText: string;
  accept: string;
  required: boolean;
  disabled: boolean;
  state: SlotState;
  onFileChange: (f: File | null) => void;
}) {
  void slot;
  return (
    <div
      className={cn(
        'rounded-md border bg-card p-3',
        disabled && 'bg-muted/20 opacity-60',
        state.uploaded && 'border-emerald-300 bg-emerald-50/50',
        state.error && 'border-destructive/40',
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-start gap-2">
          <Icon className="mt-0.5 h-4 w-4 text-muted-foreground" aria-hidden />
          <div className="min-w-0">
            <div className="flex items-center gap-1.5 text-sm font-medium text-foreground">
              {label}
              {required && <span className="text-destructive">*</span>}
              {state.uploaded && (
                <span className="inline-flex items-center gap-1 rounded-md bg-emerald-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase text-emerald-700">
                  <CheckCircle2 className="h-3 w-3" aria-hidden /> Subido
                </span>
              )}
            </div>
            <p className="text-[11px] text-muted-foreground">{helpText}</p>
          </div>
        </div>
        {!disabled && !state.uploaded && (
          <label className="inline-flex h-8 cursor-pointer items-center gap-1 rounded-md border bg-background px-2 text-xs text-muted-foreground transition-colors hover:bg-secondary">
            <Film className="h-3.5 w-3.5" aria-hidden />
            {state.file ? 'Cambiar' : 'Elegir'}
            <input
              type="file"
              accept={accept}
              className="hidden"
              onChange={(e) => {
                onFileChange(e.target.files?.[0] ?? null);
                e.currentTarget.value = '';
              }}
            />
          </label>
        )}
      </div>
      {state.file && !state.uploaded && (
        <div className="mt-2 flex items-center justify-between gap-2 rounded-md bg-muted/40 px-2 py-1 text-[11px] text-muted-foreground">
          <div className="flex min-w-0 items-center gap-1">
            <FileVideo className="h-3 w-3 shrink-0" aria-hidden />
            <span className="truncate">{state.file.name}</span>
            <span className="shrink-0 opacity-70">
              · {Math.round(state.file.size / 1024 / 1024)} MB
            </span>
          </div>
          {!disabled && (
            <button
              type="button"
              onClick={() => onFileChange(null)}
              aria-label="Quitar archivo"
              className="rounded p-0.5 hover:bg-muted"
            >
              <X className="h-3 w-3" aria-hidden />
            </button>
          )}
        </div>
      )}
      {state.progress > 0 && state.progress < 1 && !state.uploaded && (
        <div className="mt-2 h-1.5 overflow-hidden rounded-full bg-muted">
          <div
            className="h-full bg-primary transition-[width] duration-150"
            style={{ width: `${Math.round(state.progress * 100)}%` }}
          />
        </div>
      )}
      {state.error && (
        <div className="mt-2 flex items-start gap-1 text-[11px] text-destructive">
          <AlertCircle className="mt-0.5 h-3 w-3 shrink-0" aria-hidden />
          <span>{state.error}</span>
        </div>
      )}
    </div>
  );
}
